import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getLandstarOrderStatus } from "../services/mockRedemptionApi.js";
import SupportFooter from "../components/SupportFooter.jsx";
import RewardSummaryList from "../components/RewardSummaryList.jsx";
import "../styles/index.css";

const LANDSTAR_STATUS_INITIAL_RETRY_LIMIT = 6;
const LANDSTAR_STATUS_INITIAL_RETRY_DELAY_MS = 1500;
const LANDSTAR_STATUS_POLL_DELAY_MS = 2500;

function LandstarOrderConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [orderStatus, setOrderStatus] = useState(location.state?.orderStatus || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pollAttempt, setPollAttempt] = useState(0);
  const confirmationId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return location.state?.confirmationId || searchParams.get("submissionId") || "";
  }, [location.search, location.state]);
  const submissionPayload = location.state?.payload || null;
  const submittedProducts =
    submissionPayload?.products || (submissionPayload?.product ? [submissionPayload.product] : []);
  const orderStatusProducts =
    orderStatus?.selectedProducts ||
    (orderStatus?.selectedProductName
      ? [
          {
            name: orderStatus.selectedProductName,
            sku: orderStatus.selectedSku,
            image: orderStatus.selectedProductImage,
            selectedSize: orderStatus.selectedSize,
            selectedColor: orderStatus.selectedColor,
            personalization: orderStatus.personalization,
          },
        ]
      : []);
  const displayProducts = submittedProducts.length ? submittedProducts : orderStatusProducts;
  const currentStageStatus = orderStatus?.stageStatus || location.state?.stageStatus || "queued";
  const progressPercent = getLandstarConfirmationProgress(currentStageStatus, pollAttempt);
  const progressLabel = getLandstarConfirmationLabel(currentStageStatus, loading);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [location.key]);

  useEffect(() => {
    if (!confirmationId) {
      return;
    }

    let active = true;
    let timeoutId = null;

    async function loadOrderStatus(attempt = 0) {
      try {
        setLoading(true);
        setPollAttempt(attempt);
        if (attempt === 0) {
          setError("");
        }

        const response = await getLandstarOrderStatus(confirmationId);

        if (!active) {
          return;
        }

        setOrderStatus(response);
        setError("");

        if (shouldContinuePolling(response.stageStatus)) {
          timeoutId = window.setTimeout(() => {
            void loadOrderStatus(attempt + 1);
          }, LANDSTAR_STATUS_POLL_DELAY_MS);
        }
      } catch (requestError) {
        if (!active) {
          return;
        }

        const message = requestError.message || "Unable to load order status.";
        const canRetryMissingOrder =
          message === "Order not found." &&
          attempt < LANDSTAR_STATUS_INITIAL_RETRY_LIMIT;

        if (canRetryMissingOrder) {
          timeoutId = window.setTimeout(() => {
            void loadOrderStatus(attempt + 1);
          }, LANDSTAR_STATUS_INITIAL_RETRY_DELAY_MS);
        } else {
          setError(message);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    void loadOrderStatus();

    return () => {
      active = false;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [confirmationId]);

  if (!confirmationId && !submissionPayload) {
    return (
      <div className="landstar-page-shell">
        <div className="landstar-wizard-shell">
          <div className="landstar-wizard-card">
            <div className="landstar-wizard-stage">
              <div className="landstar-step-content">
                <div className="landstar-entry-card landstar-access-card">
                  <p className="landstar-kicker">Landstar Safety Awards</p>
                  <h3>No confirmation details were found.</h3>
                  <p className="landstar-intro-copy">
                    Return to the redemption flow and submit a reward request first.
                  </p>
                  <hr className="landstar-section-divider" />
                  <div className="landstar-inline-actions landstar-inline-actions-start">
                    <button
                      type="button"
                      className="landstar-primary-button"
                      onClick={() => navigate("/landstar-safety-redemption")}
                    >
                      Return to Redemption
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <SupportFooter />
      </div>
    );
  }

  return (
    <div className="landstar-page-shell">
      <div className="landstar-wizard-shell">
        <div className="landstar-wizard-card">
          <div className="landstar-wizard-header">
            <div className="landstar-wizard-intro">
              <p className="landstar-kicker">Order Confirmation</p>
              <img
                src="/logos/landstar_logo.png"
                alt="Landstar"
                className="landstar-header-logo"
              />
              <h2>Landstar Safety Awards</h2>
            </div>
          </div>

          <div className="landstar-wizard-stage">
            <div className="landstar-step-content">
              <div className="landstar-summary-card">
                <p className="landstar-kicker">Submission Received</p>
                <h3>Your reward request is in the system</h3>
                <p className="landstar-intro-copy">
                  {orderStatus?.stageStatus === "processed"
                    ? "The order was placed in Magento and the confirmation email should already be on its way."
                    : "The redemption was submitted successfully. We're tracking it now and will keep the order moving through processing."}
                </p>

                <div className="landstar-confirmation-progress" aria-live="polite">
                  <div className="landstar-confirmation-progress-meta">
                    <span className="landstar-driver-summary-label">Order Progress</span>
                    <strong>{progressLabel}</strong>
                  </div>
                  <div
                    className="landstar-confirmation-progress-track"
                    role="progressbar"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-valuenow={progressPercent}
                    aria-label="Order processing progress"
                  >
                    <span
                      className="landstar-confirmation-progress-fill"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <div className="landstar-confirmation-progress-steps">
                    <span className={progressPercent >= 25 ? "is-active" : ""}>Submitted</span>
                    <span className={progressPercent >= 65 ? "is-active" : ""}>Processing</span>
                    <span className={progressPercent >= 100 ? "is-active" : ""}>Confirmed</span>
                  </div>
                </div>

                <div className="landstar-driver-summary-row">
                  <div className="landstar-driver-summary-item landstar-driver-summary-name">
                    <span className="landstar-driver-summary-label">Confirmation</span>
                    <strong>{confirmationId}</strong>
                  </div>
                  <div className="landstar-driver-summary-item">
                    <span className="landstar-driver-summary-label">Status</span>
                    <strong>{orderStatus?.stageStatus || location.state?.stageStatus || "queued"}</strong>
                  </div>
                  {orderStatus?.magentoIncrementId ? (
                    <div className="landstar-driver-summary-item">
                      <span className="landstar-driver-summary-label">Order Number</span>
                      <strong>{orderStatus.magentoIncrementId}</strong>
                    </div>
                  ) : null}
                  {orderStatus?.submittedAt ? (
                    <div className="landstar-driver-summary-item">
                      <span className="landstar-driver-summary-label">Submitted</span>
                      <strong>{new Date(orderStatus.submittedAt).toLocaleString()}</strong>
                    </div>
                  ) : null}
                </div>

                {error ? <p className="landstar-error">{error}</p> : null}
              </div>

              {submissionPayload || orderStatus ? (
                <div className="landstar-summary-card">
                  <p className="landstar-kicker">Reward Summary</p>
                  <RewardSummaryList products={displayProducts} />

                  <div className="landstar-driver-summary-row landstar-summary-metadata">
                    <div className="landstar-driver-summary-item">
                      <span className="landstar-driver-summary-label">Driver Code</span>
                      <strong>{submissionPayload?.form?.driverCode || orderStatus?.driverCode}</strong>
                    </div>
                  </div>

                  {submissionPayload ? (
                    <>
                      <hr className="landstar-section-divider" />

                      <div className="landstar-success-grid">
                        <p>
                          <strong>Name:</strong> {submissionPayload.form.firstName}{" "}
                          {submissionPayload.form.lastName}
                        </p>
                        <p>
                          <strong>Email:</strong> {submissionPayload.form.email}
                        </p>
                        <p>
                          <strong>Phone:</strong> {submissionPayload.form.phone}
                        </p>
                        <p>
                          <strong>Address:</strong> {submissionPayload.form.address1}
                        </p>
                        <p>
                          <strong>City:</strong> {submissionPayload.form.city}
                        </p>
                        <p>
                          <strong>State:</strong> {submissionPayload.form.state}
                        </p>
                        <p>
                          <strong>ZIP:</strong> {submissionPayload.form.zip}
                        </p>
                        <p>
                          <strong>Country:</strong> {submissionPayload.form.country}
                        </p>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : null}

            </div>
          </div>
        </div>
      </div>
      <SupportFooter />
    </div>
  );
}

function shouldContinuePolling(stageStatus) {
  return stageStatus === "queued" || stageStatus === "received" || stageStatus === "processing";
}

function getLandstarConfirmationProgress(stageStatus, pollAttempt) {
  const stageProgress = {
    queued: 25,
    received: 52,
    processing: 76,
    processed: 100,
    failed: 100,
    cancelled: 100,
  };

  if (Object.prototype.hasOwnProperty.call(stageProgress, stageStatus)) {
    return stageProgress[stageStatus];
  }

  return Math.min(18 + pollAttempt * 8, 42);
}

function getLandstarConfirmationLabel(stageStatus, loading) {
  if (stageStatus === "processed") {
    return "Confirmed";
  }

  if (stageStatus === "queued" || stageStatus === "received") {
    return loading ? "Submitted" : "Submitted";
  }

  if (stageStatus === "processing") {
    return "Processing";
  }

  if (stageStatus === "failed") {
    return "Attention Needed";
  }

  return "Tracking";
}

export default LandstarOrderConfirmationPage;
